import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchTravelPlans, 
  deleteTravelPlan,
  updateTravelPlan,
  setSearchQuery,
  setSortOption,
  setTagFilter,
  setDestinationFilter,
  clearFilters,
  setFilteredPlans,
  setTimelineGroups,
  addTravelPlan,
  invalidateCache,
  clearError
} from '../store/slices/travelPlansSlice';
import { differenceInDays } from 'date-fns';

export const useTravelPlans = () => {
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const travelPlansState = useSelector((state) => state.travelPlans);

  const {
    data: travelPlans,
    filteredPlans,
    upcomingPlans,
    pastPlansByYear,
    availableTags,
    availableDestinations,
    loading,
    error,
    lastFetched,
    searchQuery,
    sortOption,
    tagFilter,
    destinationFilter,
  } = travelPlansState;

  // Load travel plans on mount or when token changes
  useEffect(() => {
    if (token) {
      dispatch(fetchTravelPlans());
    }
  }, [token, dispatch]);

  // iOS Photos-style timeline grouping function
  const groupPlansByTimeline = useMemo(() => {
    return (plans) => {
      const now = new Date();
      now.setHours(23, 59, 59, 999); // End of today
      
      const upcoming = [];
      const past = {};
      
      plans.forEach(plan => {
        const endDate = plan.endDate ? new Date(plan.endDate) : new Date(plan.startDate);
        
        if (endDate >= now) {
          // Upcoming or current travel plans
          upcoming.push(plan);
        } else {
          // Past travel plans - group by year
          const year = new Date(plan.startDate || plan.createdAt).getFullYear();
          if (!past[year]) {
            past[year] = [];
          }
          past[year].push(plan);
        }
      });
      
      // Sort upcoming plans by start date (earliest first)  
      const sortedUpcoming = [...upcoming].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      
      // Sort past plans within each year by start date (most recent first)
      Object.keys(past).forEach(year => {
        past[year] = [...past[year]].sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
      });
      
      return { upcoming: sortedUpcoming, past };
    };
  }, []);

  // Apply filters and sorting whenever filter/sort options change
  useEffect(() => {
    if (travelPlans.length === 0) return;

    let result = [...travelPlans];
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(plan => 
        plan.title.toLowerCase().includes(query) || 
        (plan.description && plan.description.toLowerCase().includes(query)) ||
        (plan.destination && plan.destination.toLowerCase().includes(query))
      );
    }
    
    // Apply tag filter
    if (tagFilter.length > 0) {
      result = result.filter(plan => 
        plan.tags && plan.tags.some(tag => tagFilter.includes(tag))
      );
    }
    
    // Apply destination filter
    if (destinationFilter) {
      result = result.filter(plan => 
        plan.destination && plan.destination === destinationFilter
      );
    }
    
    // If no filters are applied, use timeline grouping
    if (!searchQuery && tagFilter.length === 0 && !destinationFilter) {
      const timelineGroups = groupPlansByTimeline(result);
      dispatch(setTimelineGroups(timelineGroups));
      return; // Don't set filteredPlans when using timeline view
    }
    
    // Apply sorting for filtered results
    const [sortField, sortDirection] = sortOption.split('-');
    
    result = [...result].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'destination':
          comparison = (a.destination || '').localeCompare(b.destination || '');
          break;
        case 'startDate':
          comparison = new Date(a.startDate) - new Date(b.startDate);
          break;
        case 'endDate':
          comparison = new Date(a.endDate) - new Date(b.endDate);
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        default:
          comparison = new Date(a.startDate) - new Date(b.startDate);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    dispatch(setFilteredPlans(result));
  }, [travelPlans, searchQuery, sortOption, tagFilter, destinationFilter, dispatch, groupPlansByTimeline]);

  // Handlers
  const handleSearchChange = (value) => {
    dispatch(setSearchQuery(value));
  };

  const handleSortChange = (value) => {
    dispatch(setSortOption(value));
  };

  const handleTagFilterChange = (tags) => {
    dispatch(setTagFilter(tags));
  };

  const handleDestinationFilterChange = (destination) => {
    dispatch(setDestinationFilter(destination));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const handleDeletePlan = async (planId) => {
    if (window.confirm('Are you sure you want to delete this travel plan?')) {
      try {
        await dispatch(deleteTravelPlan(planId)).unwrap();
      } catch (error) {
        console.error('Error deleting travel plan:', error);
        throw error;
      }
    }
  };

  const handleUpdatePlan = async (planId, updates) => {
    try {
      await dispatch(updateTravelPlan({ planId, updates })).unwrap();
      return true;
    } catch (error) {
      console.error('Error updating travel plan:', error);
      throw error;
    }
  };

  const handleAddPlan = (newPlan) => {
    dispatch(addTravelPlan(newPlan));
  };

  const handleRefresh = () => {
    dispatch(invalidateCache());
    dispatch(fetchTravelPlans());
  };

  const handleClearError = () => {
    dispatch(clearError());
  };

  // Utility function to check if data is fresh
  const isDataFresh = useMemo(() => {
    if (!lastFetched) return false;
    const cacheTimeout = 5 * 60 * 1000; // 5 minutes
    return (Date.now() - lastFetched) < cacheTimeout;
  }, [lastFetched]);

  return {
    // Data
    travelPlans,
    filteredPlans,
    upcomingPlans,
    pastPlansByYear,
    availableTags,
    availableDestinations,
    
    // State
    loading,
    error,
    isDataFresh,
    
    // Filter state
    searchQuery,
    sortOption,
    tagFilter,
    destinationFilter,
    
    // Handlers
    handleSearchChange,
    handleSortChange,
    handleTagFilterChange,
    handleDestinationFilterChange,
    handleClearFilters,
    handleDeletePlan,
    handleUpdatePlan,
    handleAddPlan,
    handleRefresh,
    handleClearError,
    
    // Actions (for advanced usage)
    refresh: () => dispatch(fetchTravelPlans()),
    invalidateCache: () => dispatch(invalidateCache()),
  };
}; 